"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

type ToastProps = {
  id?: string
  title?: string
  message: string
  variant?: "default" | "success" | "error"
  duration?: number
}

type ToastState = ToastProps & { id: string }

type ToasterContextType = {
  toasts: ToastState[]
  addToast: (toast: Omit<ToastState, "id">) => void
  removeToast: (id: string) => void
}

const ToasterContext = React.createContext<ToasterContextType | undefined>(undefined)

export function ToasterProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastState[]>([])

  const addToast = React.useCallback((toast: Omit<ToastState, "id">) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast = { ...toast, id }

    setToasts((prev) => [...prev, newToast])

    // Auto-remove after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, toast.duration || 3000)
  }, [])

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToasterContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToasterContext.Provider>
  )
}

function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: ToastState[]
  onRemove: (id: string) => void
}) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            "pointer-events-auto flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg transition-all",
            "animate-in slide-in-from-right-full",
            "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
            toast.variant === "success" && "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950",
            toast.variant === "error" && "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950"
          )}
        >
          {toast.variant === "success" && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white text-sm font-bold">
              ✓
            </div>
          )}
          {toast.variant === "error" && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-sm font-bold">
              ✕
            </div>
          )}
          <div className="flex-1">
            {toast.title && (
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{toast.title}</p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400">{toast.message}</p>
          </div>
          <button
            onClick={() => onRemove(toast.id!)}
            className="rounded-full p-1 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}

export function useToast() {
  const context = React.useContext(ToasterContext)
  if (!context) {
    throw new Error("useToast must be used within a ToasterProvider")
  }
  return context
}
